/**
 * Reusable Settings Icon Button Component
 * Material Design 3 - WCAG 2.2 Level AA Compliant
 * 
 * Features:
 * - Consistent styling across all pages
 * - Hover, focus, active states
 * - Modified indicator badge
 * - Tooltip support
 * - Keyboard accessible (40px touch target)
 * - Smooth transitions (200ms ease-out)
 * 
 * @recommendation R2, R6, R9, R10
 */
import React from 'react';
import { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import { GLOBAL_TOOLTIP_PROPS } from '../config/tooltips.config.ts';

/** Lucide-style settings (gear) icon - stroke-based */
function SettingsGearIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

interface SettingsIconButtonProps {
  onClick: () => void;
  isOpen?: boolean;
  hasModifiedSettings?: boolean;
  tooltipText?: string;
  size?: 'small' | 'medium' | 'large';
  ariaLabel?: string;
  className?: string;
}

export function SettingsIconButton({
  onClick,
  isOpen = false,
  hasModifiedSettings = false,
  tooltipText = 'Configure settings',
  size = 'small',
  ariaLabel = 'Open settings dialog',
  className
}: SettingsIconButtonProps) {
  const [isPulsing] = useState(false);

  // R2: Icon size based on context (20px card-level, 24px page-level)
  const iconSize = size === 'large' ? 24 : 20;

  return (
    <Tooltip 
      title={tooltipText} 
      arrow 
      placement="left"
      {...GLOBAL_TOOLTIP_PROPS}
    >
      <IconButton
        onClick={onClick}
        size={size}
        aria-label={ariaLabel}
        className={className}
        sx={{
          // R9: Material Design 3 Interaction States
          color: isOpen ? '#2932E5' : '#4A5565',
          bgcolor: isOpen ? 'rgba(41, 50, 229, 0.08)' : 'transparent',
          opacity: isOpen ? 1 : 0.7,
          
          // R2: 40px touch target (WCAG 2.2 Level AA)
          width: 40,
          height: 40,
          
          // R6: Smooth transitions (200ms ease-out)
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          
          // R9: Hover state
          '&:hover': {
            color: '#2932E5',
            bgcolor: 'rgba(41, 50, 229, 0.08)',
            opacity: 1,
          },
          
          // R10: Focus state (2px focus ring)
          '&:focus-visible': {
            outline: '2px solid #2932E5',
            outlineOffset: 2,
          },
          
          // R9: Active state (scale down)
          '&:active': {
            transform: 'scale(0.95)',
          },
          
          // R9: Disabled state
          '&:disabled': {
            opacity: 0.38,
            cursor: 'not-allowed',
          },
          
          // R6: Pulse animation for first-time users (if needed)
          ...(isPulsing && {
            '@keyframes pulse': {
              '0%, 100%': { opacity: 0.7 },
              '50%': { opacity: 1, transform: 'scale(1.05)' },
            },
            animation: 'pulse 2s ease-in-out 3',
          }),
        }}
      >
        {/* R9: Badge indicator for modified settings */}
        <Badge
          variant="dot"
          color="primary"
          invisible={!hasModifiedSettings}
          sx={{
            '& .MuiBadge-dot': {
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#2932E5',
              top: 6,
              right: 6,
            },
          }}
        >
          <SettingsGearIcon size={iconSize} />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}
